import { useState } from 'react';
import dayjs from 'dayjs';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { BulkActionType, MachineUpdate } from '../types';
import { STATUSES } from '../constants';
import ActionDialog from '../../../components/ActionDialog';
import { BULK_ACTION_LABELS as LABELS } from '../../../utils/labels';

interface BulkActionDialogProps {
  action: BulkActionType;
  saving: boolean;
  onClose: () => void;
  onSave: (updates: MachineUpdate) => void;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
}

export default function BulkActionDialog({
  action,
  saving,
  onClose,
  onSave,
  minDate,
  maxDate,
}: BulkActionDialogProps) {
  const [status, setStatus] = useState<string>('to_order');
  const [statusError, setStatusError] = useState('');
  const [date, setDate] = useState<dayjs.Dayjs | null>(null);
  const [dateError, setDateError] = useState('');
  const [time, setTime] = useState<dayjs.Dayjs | null>(null);
  const [timeError, setTimeError] = useState('');

  const title = {
    status: LABELS.bulkStatus,
    start_date: LABELS.bulkStartDate,
    end_date: LABELS.bulkEndDate,
  }[action];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (action === 'status') {
      if (!status) { setStatusError(LABELS.bulkStatusRequired); return; }
      onSave({ status: status as MachineUpdate['status'] });
      return;
    }
    if (action === 'start_date') {
      let valid = true;
      if (!date) { setDateError(LABELS.bulkStartDateRequired); valid = false; }
      if (!time) { setTimeError(LABELS.bulkTimeRequired); valid = false; }
      if (!valid) return;
      onSave({ start_date: date!.format('YYYY-MM-DD'), start_time: time!.format('HH:mm') });
      return;
    }
    if (action === 'end_date') {
      let valid = true;
      if (!date) { setDateError(LABELS.bulkEndDateRequired); valid = false; }
      if (!time) { setTimeError(LABELS.bulkTimeRequired); valid = false; }
      if (!valid) return;
      onSave({ end_date: date!.format('YYYY-MM-DD'), end_time: time!.format('HH:mm') });
    }
  };

  const isDateAction = action === 'start_date' || action === 'end_date';
  const dateLabel = action === 'start_date' ? LABELS.bulkStartDateLabel : LABELS.bulkEndDateLabel;
  const timeLabel = action === 'start_date' ? LABELS.bulkStartTimeLabel : LABELS.bulkEndTimeLabel;

  return (
    <ActionDialog
      title={title}
      formId="bulk-action-form"
      saving={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
      saveLabel={LABELS.bulkDialogApply}
      cancelLabel={LABELS.bulkDialogCancel}
      testId="bulk-action-dialog"
    >
      {action === 'status' && (
        <FormControl fullWidth error={!!statusError}>
          <InputLabel id="bulk-status-label">{LABELS.bulkStatusLabel}</InputLabel>
          <Select
            labelId="bulk-status-label"
            label={LABELS.bulkStatusLabel}
            value={status}
            onChange={(e) => { setStatus(e.target.value); if (statusError) setStatusError(''); }}
            inputProps={{ 'aria-label': LABELS.bulkStatusLabel }}
          >
            {Object.entries(STATUSES).map(([key, { label }]) => (
              <MenuItem key={key} value={key} data-testid={`option-status-${key}`}>{label}</MenuItem>
            ))}
          </Select>
          {statusError && <FormHelperText>{statusError}</FormHelperText>}
        </FormControl>
      )}

      {isDateAction && (
        <div className="grid grid-cols-2 gap-3">
          <DatePicker
            label={dateLabel}
            value={date}
            onChange={(v) => { setDate(v); if (dateError) setDateError(''); }}
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!dateError,
                helperText: dateError || undefined,
              },
            }}
          />
          <TimePicker
            label={timeLabel}
            ampm={false}
            minutesStep={30}
            timeSteps={{ minutes: 30 }}
            value={time}
            onChange={(v) => { setTime(v); if (timeError) setTimeError(''); }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!timeError,
                helperText: timeError || undefined,
              },
            }}
          />
        </div>
      )}
    </ActionDialog>
  );
}
